import { BaseComponentRegistry } from "../BaseComponentRegistry";
import { useComponentRegistry } from "../Hooks/useComponentRegistry";

/**
 * Extracts the component ID type from a registry.
 */
type ExtractRegistryComponentId<T> = T extends BaseComponentRegistry<infer C, any> ? C : never;

/**
 * Extracts the props type from a registry.
 */
type ExtractRegistryProps<T> = T extends BaseComponentRegistry<any, infer P> ? P : never;

/**
 * Type for the BaseComponentRegistry instance returned by useComponentRegistry.
 * This is used to ensure type safety when creating registry wrapper components.
 */
type BaseComponentRegistryType = ReturnType<typeof useComponentRegistry>;

/**
 * Props for the generic component registry wrapper.
 * It includes a factory function to get the registry instance and the props to pass to the components.
 */
interface IComponentRegistryWrapperProps<TProps = any> {
    registryFactory: () => BaseComponentRegistryType;
    props: TProps;
}

/**
 * Generic wrapper component for rendering all components in a registry.
 */
function ComponentRegistryWrapper<TProps = any>({
    registryFactory,
    props
}: Readonly<IComponentRegistryWrapperProps<TProps>>): JSX.Element {
    const registry = useComponentRegistry(registryFactory); // this hook is needed to trigger re-renders when the modules context changes
    return <>{registry.render(props)}</>;
}

/**
 * Create a registry wrapper component that uses the provided factory function to get the registry instance.
 * This function returns a component that renders all components registered in the registry with the given props.
 * @param registryFactory Function to get the singleton instance of the registry.
 * @returns A React component that renders all components in the registry with the provided props.
 */
function createRegistryListView<TRegistry extends BaseComponentRegistryType>(
    registryFactory: () => TRegistry
): (props: Readonly<ExtractRegistryProps<TRegistry>>) => JSX.Element {
    return function RegistryWrapper(props: Readonly<ExtractRegistryProps<TRegistry>>): JSX.Element {
        return (
            <ComponentRegistryWrapper
                registryFactory={registryFactory}
                props={props}
            />
        );
    };
}

/**
 * Create a registry wrapper component that uses the provided factory function to get the registry instance.
 * This function returns a component that renders a single component registered in the registry based on the componentId prop.
 * @param registryFactory Function to get the singleton instance of the registry.
 * @returns A React component that renders a single component in the registry with the provided props.
 */
function createRegistrySingleView<TRegistry extends BaseComponentRegistryType>(
    registryFactory: () => TRegistry
): (props: Readonly<ExtractRegistryProps<TRegistry>>) => JSX.Element {
    return function RegistryWrapper(props: Readonly<ExtractRegistryProps<TRegistry> & { componentId: ExtractRegistryComponentId<TRegistry> }>): JSX.Element {
        return (
            <ComponentRegistryWrapper
                registryFactory={registryFactory}
                props={props}
            />
        );
    };
}

export { createRegistryListView, createRegistrySingleView };
